/**
 * Inline alert.
 *
 * Used for form level feedback: a rate limited send, an invalid code, or a
 * degraded recommendation round.
 */

import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentProps } from 'react'

import { cn } from '../../lib/format'

const alertVariants = cva('flex flex-col gap-1 rounded-lg border p-3 text-sm', {
  variants: {
    variant: {
      default: 'border-border bg-muted/50 text-foreground',
      info: 'border-border bg-accent/60 text-accent-foreground',
      success: 'border-success/40 bg-success/10 text-foreground',
      warning: 'border-warning/50 bg-warning/15 text-warning-foreground',
      destructive: 'border-destructive/40 bg-destructive/10 text-destructive',
    },
  },
  defaultVariants: { variant: 'default' },
})

export interface AlertProps
  extends ComponentProps<'div'>,
    VariantProps<typeof alertVariants> {}

export function Alert({ className, variant, ...props }: AlertProps) {
  return <div role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
}

export function AlertTitle({ className, ...props }: ComponentProps<'p'>) {
  return <p className={cn('font-medium leading-tight', className)} {...props} />
}

export function AlertDescription({ className, ...props }: ComponentProps<'p'>) {
  return <p className={cn('text-sm leading-relaxed opacity-90', className)} {...props} />
}
